<template>
  <main id="main-content" class="flex-display-body container py-5">
    <!--suppress JSUnresolvedVariable onExpirationSelected in renew-group-mixin -->
    <group-renew-modal modal-id="group-batch-expiration-modal"
                            :group-ids="groupIdsToRenew"
                            @groupExpirationSelected="onBatchRenewSelected">
    </group-renew-modal>
    <div id="group-batch-renew" class="row py-5" role="tabpanel" aria-labelledby="groupBatchRenew">
      <div class="col-12">
        <b-alert id="alert-group-batch-expiration-success" v-if="showMessage && updateResponse.success"
                 variant="success" dismissible show fade
        >
          Successfully updated {{updateResponse.success.length}} groups.
        </b-alert>
        <b-alert id="alert-group-batch-expiration-errors" v-if="showMessage && updateResponse.errors.length"
                 variant="danger" dismissible show fade
        >
          <span class="bold">The following errors occurred:</span>
          <div class="scroll-container">
            <ul v-for="(error) in updateResponse.errors" :key="error.instance">
              <li>{{error.instance}} - {{error.code}} : {{error.message}}</li>
            </ul>
          </div>
        </b-alert>
        <div class="card mb-4">
          <div class="card-body">
            <h3 class="card-title">Batch Renew</h3>
            <!-- Limiting page-options to a max of 50 to reduce the chance of timeout errors while waiting for the
            backend to renew the selected groups. -->
            <table-options-filter table-id="tbl-edit-members"
                                  :per-page="tablePerPage"
                                  @perPageChanged="onPerPageChanged"
                                  @filterChanged="onFilterChanged">
            </table-options-filter>
            <b-row>
              <b-col style="margin-left: 0.75rem">
                <b-form-checkbox id="chk-group-select_all" v-model="selectAll"
                                 v-on:click.native.stop="selectAllGroups"
                                 :disabled="submittingGroupRenew">
                  <small>Select all groups.</small>
                </b-form-checkbox>
              </b-col>
            </b-row>
            <div class="group-table-loading-wrapper" :class="[ fetchingGroups ? 'group-table-loader' : '']">
              <p class="sr-only" v-if="fetchingGroups">Content is loading...</p>
              <p class="sr-only" v-else>Content has finished loading.</p>
              <b-table
                id="tbl-groups-batch-renew"
                show-empty
                striped
                stacked="md"
                sort-by="_expirationDate"
                :items="administeredGroups"
                :fields="fields"
                :current-page="currentPage"
                :per-page="tablePerPage"
                :filter="tableFilter"
                v-model="inTable"
                @filtered="onFiltered"
              >
                <template slot="checkbox" slot-scope="row">
                  <div class="custom-control custom-checkbox custom-control-inline">
                    <label :for="'chk-row-'+row.item.uugid" class="sr-only">{{row.item.uugid}}</label>
                    <!--suppress HtmlFormInputWithoutLabel lint checks do not pick up the dynamic id-->
                    <input :disabled="submittingGroupRenew"
                           type="checkbox"
                           :id="'chk-row-'+row.item.uugid"
                           :key="row.index"
                           :value="row.item.uugid"
                           v-model="groupIdsToRenew"
                           :aria-label="'Select/de-select '+row.item.uugid"
                           class="custom-control-input"/>
                    <label :for="'chk-row-'+row.item.uugid"
                           :aria-label="row.item.uugid"
                           class="custom-control-label">
                    </label>
                  </div>
                </template>
                <template slot="empty">
                  <!--Currently, this slot won't show due to a bug in Bootstrap-Vue
                  if filter is set to a function (doFilter). Defaults to emptyfiltered slot.-->
                  <!--See https://github.com/bootstrap-vue/bootstrap-vue/issues/1517-->
                  You are not an administrator of any groups.
                </template>
                <template slot="_expirationDate" slot-scope="row">
                  <div :id="row.item._expElementId">{{row.item._expString}}</div>
                </template>
              </b-table>
            </div>
            <b-row>
              <b-col md="6" class="my-1">
                <b-pagination id="mnu-pagination" :total-rows="totalRows" :per-page="tablePerPage"
                              v-model="currentPage" class="my-0">
                </b-pagination>
              </b-col>
            </b-row>
            <div class="card-footer">
              <div class="d-flex flex-wrap justify-content-around">
                <b-button v-b-modal.group-batch-expiration-modal
                          id="btn-batch-renew-groups"
                          class="mt-4"
                          type="button"
                          variant="primary"
                          :disabled="groupIdsToRenew.length === 0 || submittingGroupRenew">
                  {{ submittingGroupRenew ? 'Submitting...' : 'Choose Expiration' }}
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>

</template>

<script>
import tableMixin from '../util/table-mixin';
import { renewGroup } from '../util/renew-group-mixin';
import { groupsFetch, groupsMixin } from '../util/groups-mixin';

export default {
  /* eslint-disable no-underscore-dangle */
  name: 'GroupBatchRenew',
  title: 'Batch Renew',
  mixins: [tableMixin, groupsFetch, renewGroup, groupsMixin],
  data() {
    return {
      selectAll: false,
      inTable: [],
      showMessage: false,
      updateResponse: {
        success: [],
        errors: [],
      },
      updateAlertVariant: 'success',
      fields: [
        {
          key: 'checkbox',
          label: 'Select',
          sortable: false,
          thClass: 'tbl-col-header-checkbox-label',
        },
        {
          key: 'uugid',
          label: 'Group',
          sortable: true,
        },
        {
          key: '_expirationDate',
          label: 'Expires',
          tdClass: 'expirationClass',
          sortable: true,
        },
      ],
      // Override default values for userRoles and withRoles from groupsFetch mixin
      userRoles: ['administrator'],
      withRoles: ['administrators'],
    };
  },
  computed: {
    administeredGroups() {
      return this.$store.getters.mappedGroupsForListing.filter(group => group._isAdmin);
    },
  },
  methods: {
    onBatchRenewSelected(date, groupIds) {
      const vm = this;
      vm.showMessage = false;
      vm.updateResponse.success = [];
      vm.updateResponse.errors = [];
      vm.submittingGroupRenew = true;
      vm.$store.dispatch('doBatchExpirationUpdate', { groupIds, expiration: date })
        .then((resp) => {
          const response = resp.data;
          vm.updateResponse.success = response.successfulUpdates;
          vm.updateResponse.errors = response.errors;
          vm.$store.commit('clearGroups');
          vm.fetchingGroups = true;
          if (vm.updateResponse.success.length > 0) {
            return vm.fetchGroups();
          }
        })
        .then(() => {
          vm.showMessage = true;
          vm.scrollToElement('main-content');
        })
        .finally(() => {
          vm.clearSelectedGroups();
          vm.fetchingGroups = false;
          vm.groupIdsToRenew = [];
          vm.submittingGroupRenew = false;
        });
    },
    selectAllGroups() {
      if (!this.submittingGroupRenew) {
        this.groupIdsToRenew = [];
        if (!this.selectAll) {
          this.inTable.forEach((group) => {
            this.groupIdsToRenew.push(group.uugid);
          });
        }
      }
    },
    clearSelectedGroups() {
      this.selectAll = false;
      this.groupIdsToRenew = [];
    },
  },
  watch: {
    tablePerPage(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.clearSelectedGroups();
      }
    },
    currentPage(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.clearSelectedGroups();
      }
    },
    tableFilter(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.clearSelectedGroups();
      }
    },
  },
  created() {
    // NOTE: Groups are fetched in the groupsFetch mixin when the component is created.
    const vm = this;
    // Here, we are utilizing the $nextTick method to wait for the DOM to update (which will happen after the mixin's
    // created lifecycle method is completed) before we execute the code within the component's created lifecycle method
    vm.$nextTick(() => {
      if (!vm.fetchingGroups) {
        vm.updateResponse.success = [];
        vm.updateResponse.errors = [];
        window.addEventListener('resize', vm.handleResize);
        vm.handleResize();
      }
    });
  },
};
</script>

<style>
  /*noinspection CssUnusedSymbol*/
  @media (min-width: 769px) {
    .tbl-col-header-checkbox-label {
      color: transparent !important;
      font-size: .25em;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/GroupBatchRenew.vue